import React, { useEffect, useState } from 'react';
import ButtonAuth from '../../../components/ButtonAuth';
import Input from '../../../components/Input';
import rocketBoyImg from '../../../assets/images/barbfullLogo.png';
import { Controller, useForm } from 'react-hook-form';
import AuthHeader from '../../../components/header/AuthHeader';
import AuthWrapper from '../../../components/AuthWrapper';
import BannerRight from '../../../components/BannerRight';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTrackingContext } from '../../../context/trackingContext';
import Footer from '../../../components/Footer';
import useClient from '../../../hooks/useClient';
import config from '../../../config';
import Alert from '../../../components/Alert';

const ResetPassword = () => {
  const { trackPageView, trackFormStart, trackFormSubmit, trackError, setAlert } =
    useTrackingContext();

  useEffect(() => {
    trackPageView({
      pageName: 'Reset password page'
    });
  }, []);

  const client = useClient();
  const location = useLocation();
  const [showAlert, setShowAlert] = useState(false);

  const {
    control,
    handleSubmit,
    watch,
    formState: { isSubmitting, isValid, isDirty }
  } = useForm({ mode: 'onTouched' });

  const navigate = useNavigate();

  useEffect(() => {
    isDirty &&
      trackFormStart({
        formName: 'Reset password form start'
      });
  }, [isDirty]);

  const onSubmit = async (data) => {
    console.log(data);

    trackFormSubmit({
      formName: 'Reset Password form Submit'
    });

    try {
      const token = new URLSearchParams(location.search).get('token');

      await client.post(`${config.everywhereApi}/users/self/recovery/reset-password`, {
        password: data.password,
        token
      });

      setAlert('passwordReset');
      navigate('/login');
    } catch (err) {
      const error = err?.response?.data?.error;

      trackError({ error_text: error?.errorMessage });

      setShowAlert({
        text: error ? error?.errorMessage + ' - ' + error.errorCode : 'Unknown error',
        success: false
      });
    }

    trackFormSubmit({
      formName: 'Reset password submit'
    });
  };

  return (
    <div className="flex h-screen overflow-hidden">
      <div className="w-full lg:w-1/2 relative overflow-y-scroll left-form">
        <AuthHeader />
        <AuthWrapper className="max-w-sm">
          <>
            <form onSubmit={handleSubmit(onSubmit)}>
              <h1 className="font-Korolev-Bold text-4xl xl:text-6xl text-black uppercase lg:leading-8 xl:leading-[2.75rem] mb-4">
                RESET <br /> PASSWORD
              </h1>

              <img src={rocketBoyImg} alt="build rocket boy" className="w-full mb-5" />

              <p className="text-base text-black mb-5">
                Enter and confirm your new password below.{' '}
              </p>

              <div className="mb-5">
                <Controller
                  defaultValue=""
                  control={control}
                  name="password"
                  rules={{
                    required: 'Password is required',
                    minLength: { value: 8, message: 'Password must be at least 8 characters long' },
                    pattern: {
                      value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]+$/,
                      message:
                        'Password must contain at least one lowercase letter, one uppercase letter, one number, and one of the following special characters: @,$,!,%,*,?, and &. Other special characters are not accepted.'
                    }
                  }}
                  render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                    <Input
                      type="password"
                      placeholder="Password"
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      className="w-full"
                      passwordVisiblity
                      questionIcon={true}
                      tooltip={
                        <>
                          <h2 className="font-Korolev-Bold text-base">PASSWORD REQUIREMENTS:</h2>
                          <ul className="list-disc text-[13px] pl-2.5 list-inside marker:text-lg">
                            <li>
                              <span className="-ml-2">1 Letter, 1 Capital Letter, 1 Number</span>
                            </li>
                            <li>
                              <span className="-ml-2">
                                1 Special Character: @, $, !, %, *, ? and & only
                              </span>
                            </li>

                            <li>
                              <span className="-ml-2">
                                No character may repeat more than 2 times
                              </span>
                            </li>

                            <li>
                              <span className="-ml-2">Between 8 and 32 characters in length</span>
                            </li>
                          </ul>
                        </>
                      }
                      error={error}
                    />
                  )}
                />
              </div>

              <div className="mb-5">
                <Controller
                  defaultValue=""
                  control={control}
                  name="rePassword"
                  rules={{
                    required: true,
                    validate: (value) => value === watch('password') || 'Passwords do not match'
                  }}
                  render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                    <Input
                      type="password"
                      placeholder="Confirm Password"
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      className="w-full"
                      passwordVisiblity
                      questionIcon={false}
                      error={error}
                    />
                  )}
                />
              </div>

              <ButtonAuth
                label={'submit'}
                className="mb-3 lg:mb-5"
                type={'submit'}
                isSubmitting={isSubmitting}
                isDisabled={!isValid}
              />
            </form>
            {showAlert && (
              <Alert setShowAlert={setShowAlert} success={false}>
                <p className="pr-12 text-sm md:text-base">
                  Password Reset Failed: {showAlert.text}
                </p>
              </Alert>
            )}
          </>
        </AuthWrapper>
        <Footer />
      </div>
      <BannerRight />
    </div>
  );
};

export default ResetPassword;
